import { type ReactNode, useContext, useEffect, useState } from 'react';

import { Box, Card, Image, Text } from '@zillow/constellation-10';

import { DISPLAY_SERVER_ERROR_AT, EXIT_CTA_LABEL } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import UnitItem from './components/UnitItem';

import celebrateIllustration from '../../../images/celebrate.svg';

import { WizardContext } from '../../Wizard/context';
import PageWizard, { type PageWizardProps } from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const MultiplexOverview = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  const handleEditClick = () => {
    // TODO: handle specific unit event
    onGoToNextStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideBackButton={true}
      hideFooter={true}
      {...rest}
    >
      <Box
        css={{
          margin: '0 auto',
          maxWidth: '800px',
          width: '100%',
        }}
        data-testid="MultiplexOverview"
      >
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '400',
          }}
        >
          <Box
            data-testid="Address"
            css={{
              display: 'flex',
              flexDirection: 'column',
              // marginBottom: '400',
            }}
          >
            <Text textStyle="heading-md" data-testid="AddressStreet">
              5210 Keystone PI N
            </Text>
            <Text textStyle="heading-md" data-testid="AddressCityStateZip">
              Seattle, WA 98102
            </Text>
          </Box>

          <Card css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '400' }}>
            <Image
              src={celebrateIllustration}
              alt="Celebration illustration"
              css={{ width: '120px', height: '120px', flexShrink: 0, objectFit: 'cover' }}
            />
            <Box css={{ display: 'flex', flexDirection: 'column' }}>
              <Text textStyle="body-bold">Great job adding your property details!</Text>
              <Text>
                Now you can review and post individual units. Edit each unit to add photos, pricing, and availability,
                then publish when you&apos;re ready.
              </Text>
            </Box>
          </Card>

          <Box
            data-testid="UnitsListContainer"
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
              gap: '200',
            }}
          >
            <Text textStyle="body-bold">Units (5)</Text>
            <Box data-testid="UnitsList" css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              {Array.from({ length: 5 }, (_, index) => (
                <UnitItem
                  key={index}
                  unitName={`Unit ${index + 1}`}
                  unitType="Studio"
                  status="Draft"
                  onEditClick={handleEditClick}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWizard>
  );
};

export default MultiplexOverview;
